/**
 * The console's one way to talk to the API: same origin (the Worker
 * serves both), the session cookie carries who. Every error is an
 * ApiError with the API's {code, message, request_id}.
 */
export class ApiError extends Error {
  status: number; code: string; requestId?: string; extra: Record<string, unknown>;
  // Most refusals arrive as {error:{code,message,request_id}}, but not
  // all of them: POST /v1/auth/session answers `no_memberships` with a
  // bare {status, message} — its 403 is about the person, not the
  // request. Reading only the envelope turned that into "HTTP 403" and
  // threw away the one sentence that explains what to do about it.
  constructor(status: number, body: { error?: { code?: string; message?: string; request_id?: string; [k: string]: unknown }; status?: string; message?: string } | null) {
    const e = body?.error ?? {};
    super(e.message ?? body?.message ?? `HTTP ${status}`);
    this.status = status; this.code = e.code ?? body?.status ?? 'http_error'; this.requestId = e.request_id; this.extra = e;
  }
}

/**
 * A refusal the reader cannot act on: they lack the scope, not the
 * network. `insufficient_scope` is the API's code for it; a bare 403 is
 * treated the same way, since every other 403 here is also "not yours".
 */
export function isForbidden(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 403 || e.code === 'insufficient_scope');
}

export interface ApiResponse<T> { data: T; etag: string | null; headers: Headers }

async function call<T>(method: string, path: string, { body, headers = {}, raw }: { body?: unknown; headers?: Record<string, string>; raw?: BodyInit } = {}): Promise<ApiResponse<T>> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: { accept: 'application/json', ...(body !== undefined ? { 'content-type': 'application/json' } : {}), ...headers },
    body: raw ?? (body !== undefined ? JSON.stringify(body) : undefined),
  });
  const text = await res.text();
  let data: unknown = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) throw new ApiError(res.status, (data ?? null) as never);
  return { data: data as T, etag: res.headers.get('etag'), headers: res.headers };
}

export const api = {
  get: <T,>(p: string, o?: { headers?: Record<string, string> }) => call<T>('GET', p, o),
  post: <T,>(p: string, body?: unknown, o?: { headers?: Record<string, string> }) => call<T>('POST', p, { body, ...o }),
  put: <T,>(p: string, body?: unknown, o?: { headers?: Record<string, string> }) => call<T>('PUT', p, { body, ...o }),
  patch: <T,>(p: string, body?: unknown, o?: { headers?: Record<string, string> }) => call<T>('PATCH', p, { body, ...o }),
  del: <T,>(p: string, body?: unknown, o?: { headers?: Record<string, string> }) => call<T>('DELETE', p, { body, ...o }),
  upload: <T,>(p: string, form: FormData) => call<T>('POST', p, { raw: form }),
};

export interface Me {
  id: string; label: string; kind: string;
  user?: { id: string; email: string; name: string | null; global_role: string | null; clients: '*' | string[]; memberships: Array<{ client_id: string; role: string; site_key: string | null }> };
}
/** `settings` is the client's free-form JSON column (PATCH /v1/clients/:id).
 *  `logo` is a convention, not a schema: an absolute URL to a square mark. */
/** `clerk_org_id` is how the organisation switcher finds the Clerk
 *  organisation behind a client: null for one Clerk never made, and NOT
 *  derivable from `id` — an org made by hand carries a slug Clerk
 *  invented from the name. */
export interface Client { id: string; name: string; clerk_org_id?: string | null; settings?: { logo?: string | null } & Record<string, unknown> }
export interface Member { id: string; email: string | null; name: string | null; role: 'admin' | 'editor'; site_key: string | null; pending: boolean }
/** `name` (what the site calls itself) and `mark` (its apple touch icon)
 *  are lifted out of the site's current bundle by the sites list, so a
 *  list can be drawn without a bundle fetch per row. Both are null for a
 *  site with no bundle yet — fall back to `key`, which always exists. */
export interface Site { key: string; domain: string; live: number; droplet?: string | null; pool?: string | null; region?: string | null; current_version: number; wp_domain?: string | null; name?: string | null; mark?: string | null }
/**
 * A site's posts, from its datahouse (#129): the site's own answer,
 * proxied. `views` is present only when the list was asked for a
 * window — null when the site has no Plausible key, zeros for a post
 * Plausible did not see in that window.
 */
export interface SitePost {
  wp_id: number;
  title: string | null;
  slug: string | null;
  link: string | null;
  path: string | null;
  status: 'publish' | 'future' | 'draft' | 'pending' | 'private' | 'trash' | string;
  date_gmt: string | null;
  modified_gmt: string | null;
  author: { wp_id: number; name: string | null } | null;
  views?: { pageviews: number; visitors: number } | null;
}
export interface SitePostsResponse {
  posts: SitePost[];
  /** null for a search: a ranked candidate set has no cheap count. */
  total: number | null;
  page: number;
  per_page: number;
  views_range?: string;
  views_connected?: boolean;
  /** Where the views came from: the site's traffic source. */
  views_source?: TrafficSource;
}
/** The days of a writer's week, Monday first, as a rota names them. */
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof WEEKDAYS)[number];
/**
 * A writer's rota, stored under their Newsroom key: the weekdays worked,
 * dated days off (leave) and dated extra days worked outside the
 * pattern; `name` the display name it was set under, so a person with
 * no articles in a window is still named.
 */
export interface NewsroomRota {
  name?: string;
  days: Weekday[];
  off: string[];
  on: string[];
}
/** The Newsroom's target (`/v1/clients/:c/newsroom/settings`): posts a day per site, the timezone days are counted in, and each writer's rota. */
export interface NewsroomSettings {
  timezone: string;
  target_per_day: number;
  /** Per-site daily targets; a site not listed follows `target_per_day`. */
  sites: Record<string, number>;
  /** Sites taken out of the Newsroom (still live, nobody publishing on them for now); a site not listed is watched. */
  inactive: Record<string, true>;
  /** Each writer's rota by their key (authorKey of the display name); empty for a caller narrowed to their own site. */
  authors: Record<string, NewsroomRota>;
  /** Bylines marked shared accounts (not people), by the same key, with the name each was marked under; empty for a narrowed caller. */
  accounts: Record<string, { name?: string }>;
}
export const NEWSROOM_DAYS = [1, 7, 14, 28] as const;
export type NewsroomDays = (typeof NEWSROOM_DAYS)[number];
interface NewsroomAuthorCount { author: { wp_id: number; name: string | null } | null; count: number; by_day?: Array<{ day: string; count: number }> }
/**
 * One site's row. `ok` carries the site's own answer; anything else says
 * why there is none: off the datahouse (never asked), its image predates
 * the summary route, its store is still backfilling, no admin secret, or
 * it did not answer.
 */
export interface NewsroomSite {
  key: string;
  name: string;
  domain: string;
  wp_domain: string | null;
  /** The site's square image (favicon, else app icon); null where it has none, absent from an older API. */
  mark?: string | null;
  target_per_day: number;
  state: 'ok' | 'not_datahouse' | 'needs_update' | 'store_not_ready' | 'no_secret' | 'unreachable';
  reason?: string;
  /** Search and page-view days from the nightly import; absent on a site off the datahouse, null where the API has no import to read. */
  traffic?: NewsroomTraffic | null;
  published?: { total: number; truncated: boolean; by_day: Array<{ day: string; count: number }>; by_author: NewsroomAuthorCount[] };
  /** What it published in the previous period (`NewsroomResponse.previous`); null where that read failed. */
  published_previous?: number | null;
  pipeline?: { future: number; pending: number; draft: number; missed_schedule: number };
  scheduled?: SitePost[];
  pending?: SitePost[];
  last_published_gmt?: string | null;
  posts?: SitePost[];
  posts_truncated?: boolean;
}
/** One day's search and traffic figures; null where the import does not hold that day (never zero). */
/** `visitors`: that day's unique visitors (Plausible visitors, GA4 users); absent from an older API. */
export interface NewsroomTrafficDay { day: string; impressions: number | null; clicks: number | null; pageviews: number | null; visitors?: number | null; /** The day's average Search position, over its impressions. */ position?: number | null }
/** A site's Search Console impressions and clicks, and its traffic source's page views, per window day; `search` false where the reader may not see Search Console (the figures are then null, not merely unheld). */
/** `previous`: the previous period's days, paired by index with `by_day`. */
export interface NewsroomTraffic { source: 'plausible' | 'ga4'; search: boolean; by_day: NewsroomTrafficDay[]; previous?: NewsroomTrafficDay[] }
/**
 * A person across the network: the same display name on every site is
 * the same person. `working_days` are the window's days they are
 * rostered for, every day where they have no `rota`; a person with a
 * rota who published nothing in the window is listed at zero with no
 * sites (for a reader who sees the whole organisation).
 */
export interface NewsroomAuthor {
  key: string;
  name: string;
  total: number;
  /** `by_day`: the site's own count of their posts per local day, where the site's image sends it. */
  sites: Array<{ site: string; wp_id: number; count: number; by_day?: Array<{ day: string; count: number }> }>;
  rota: NewsroomRota | null;
  working_days: string[];
}
/**
 * A byline marked a shared account: a site's default author for link
 * articles, a newsdesk. Not a person, so apart from `authors`; its
 * articles still count for its sites. Listed at zero (no sites) for an
 * org-wide reader when it published nothing, so it can be unmarked.
 */
export interface NewsroomAccount {
  key: string;
  name: string;
  total: number;
  sites: Array<{ site: string; wp_id: number; count: number }>;
}
/** What E-E-A-T asks of an author page that this one lacks (`/newsroom/profiles`). */
type ProfileGap = 'photo' | 'job_title' | 'bio' | 'short_bio' | 'social' | 'author_page_noindex' | 'email_slug';
/** A writer's public WordPress profile, read once per person: the same on every site of a network. */
export interface AuthorProfile {
  state: 'ok' | 'not_public' | 'not_found' | 'wp_unavailable';
  reason?: string;
  name?: string | null;
  slug?: string | null;
  link?: string | null;
  /** A real picture's URL; null when only Gravatar's grey default is there. */
  avatar?: string | null;
  photo?: 'gravatar' | 'custom' | 'none' | 'unknown';
  job_title?: string | null;
  bio?: string | null;
  same_as?: string[];
  noindex?: boolean;
  last_published?: string | null;
  missing?: ProfileGap[];
}
export interface AuthorProfilesResponse { profiles: Record<string, AuthorProfile> }
/** The longest custom Newsroom range, in days (the API's NEWSROOM_MAX_DAYS). */
export const NEWSROOM_MAX_DAYS = 92;
/** How far back a custom range may start, in days before today (the API's NEWSROOM_EARLIEST_DAYS). */
export const NEWSROOM_EARLIEST_DAYS = 366;
/** One person's Plausible pageviews in the window (`/v1/clients/:c/newsroom/authors`), merged by name as NewsroomAuthor is. */
export interface AuthorViews {
  key: string;
  name: string;
  pageviews: number;
  visitors: number;
  sites: Array<{ site: string; name: string; pageviews: number; visitors: number }>;
}
export interface AuthorViewsResponse {
  window: NewsroomWindow;
  timezone: string;
  dates: [string, string];
  /** Each site's answer: `ok`, or why there is none (no_key, plausible_disabled, a Plausible refusal's code, unreachable). */
  sites: Array<{ key: string; name: string; state: string; reason?: string }>;
  authors: AuthorViews[];
  /** Pageviews with no author (pages that are not articles), per site. */
  unattributed: Array<{ site: string; pageviews: number }>;
  totals: { pageviews: number; unattributed_pageviews: number; sites: number };
}
/**
 * The days a Newsroom answer covers. A preset window ends today and runs to
 * now; a custom range may end earlier, and then `today`/`today_since` are its
 * last day and `ends_today` is false.
 */
export interface NewsroomWindow { since: string; until: string; tz: string; days: string[]; today: string; today_since: string; ends_today?: boolean }
export interface NewsroomResponse {
  window: NewsroomWindow;
  /** The period compared against: as many days just before, to the same point in the last; absent from an older API. */
  previous?: { since: string; until: string; days: string[] };
  timezone: string;
  target_per_day: number;
  sites: NewsroomSite[];
  /** Sites out of the Newsroom: never asked, counted nowhere, named so they can be found. */
  inactive: Array<{ key: string; name: string; domain: string; mark?: string | null }>;
  authors: NewsroomAuthor[];
  accounts: NewsroomAccount[];
  totals: { published: number; target: number; sites: number };
}
/**
 * The site's datahouse dead-letter queue, explained (headless#374,
 * #149): objects WordPress has that the site could not sync. `class`,
 * `summary` and `action` are the site's own words — what happened and
 * what a person does next; `reason` is the raw diagnostic. `stale` is
 * past the automatic-retry horizon (`horizon_days`), waiting on a
 * person. `title`/`path` come from the store where it holds the
 * document; an object that never landed has neither.
 */
export type SyncFailureClass = 'origin-empty' | 'origin-missing' | 'connector-auth' | 'origin-unavailable' | 'served-from-cache' | 'store-only' | 'incomplete' | 'body-unreadable' | 'comment-count' | 'other';
export interface SyncFailedRow {
  type: string; wp_id: number; kind: string | null;
  class: SyncFailureClass; summary: string; action: string; reason: string;
  attempts: number; first_seen: string; last_seen: string; next_retry_at: string; stale: boolean;
  title: string | null; slug: string | null; path: string | null;
}
export interface SyncFailedResponse {
  count: number; stale: number; oldest: string | null; horizon_days: number;
  by_class: Partial<Record<SyncFailureClass, number>>;
  rows: SyncFailedRow[]; total: number; page: number; per_page: number; truncated: boolean;
}
/**
 * How the site's datahouse store is keeping up with WordPress (`…/sync`,
 * the site's own /api/admin/sync). `ready: false` is a store still on its
 * first backfill. `last_sweep_at` is the last sweep that applied every
 * change it saw; `lag_seconds` is 0 after one, else the age of the oldest
 * change the last sweep could not apply. `transport` is what the site has
 * learned about its connector since it started (`probed: false` until
 * something has pulled). `timestamp` is the site's clock.
 */
export interface SiteSync {
  timestamp: string;
  content_source: 'datahouse';
  ready: boolean;
  last_sweep_at: string | null;
  lag_seconds: number | null;
  transport: { probed: boolean; connector: boolean | null; objects: boolean | null; version: string | null; probed_at: string | null; reason: string | null };
}
interface SyncRetryResult { type: string; wp_id: number; kind: string | null; outcome: 'cleared' | 'deferred'; reason: string | null; class?: SyncFailureClass; summary?: string; action?: string }
export interface SyncRetryResponse { retried: number; cleared: number; deferred: number; remaining: number; results: SyncRetryResult[]; not_found: Array<{ type: string; wp_id: number }> }
/** The console's ranges. `today` is midnight UTC to now (NOT Plausible's `day`, which is the site's own day — api/src/stats.mjs); there is no 90d — 91d is the nearest. */
export const STATS_RANGES = ['today', '7d', '28d', '30d', '91d', 'all'] as const;
export type StatsRange = (typeof STATS_RANGES)[number];
export const RANGE_LABELS: Record<StatsRange, string> = { today: 'Today', '7d': '7 days', '28d': '28 days', '30d': '30 days', '91d': '91 days', all: 'All time' };
/** The six topline metrics, in the order the console shows them. */
export interface ToplineMetrics {
  visitors: number; visits: number; pageviews: number;
  views_per_visit: number; bounce_rate: number; visit_duration: number;
}
/** `connected: false` means no key is stored — not an error, an invitation. */
/** Which numbers the console shows for a site (docs/SEARCH-PLAN.md). */
export type TrafficSource = 'ga4' | 'plausible';
/** GET …/stats/source. `setting` null is the default: GA4 once a GA4 property is chosen, else Plausible. */
export interface TrafficSourceInfo {
  setting: TrafficSource | null;
  source: TrafficSource;
  ga4: { connected: boolean; property: string | null };
  plausible: { connected: boolean; disabled: boolean };
}
export interface SiteStats {
  range: StatsRange;
  connected: boolean;
  /** The source these numbers came from (or would have). */
  source?: TrafficSource;
  /** For GA4: the property read. */
  property?: string;
  /** The site's own config has Plausible off: no tag, nothing to query. */
  disabled?: boolean;
  site_id?: string;
  metrics?: ToplineMetrics;
  /** The equally long window before this one; null for `all`. */
  previous?: ToplineMetrics | null;
  /** Percentage change per metric; null per metric where it cannot be stated. */
  change?: Record<keyof ToplineMetrics, number | null> | null;
  /** The compared window as [start, end], for saying what it was. */
  compared_to?: [string, string] | null;
  cached?: boolean;
  /** RESOLVED: this site's own number, or the organisation's default. */
  ecpm: number | null;
  fill_rate: number | null;
  ads_per_pageview: number | null;
  /** What THIS site sets, if anything. Null where it inherits. */
  own?: EstimateInputs;
  /** The organisation's defaults, which the blanks above fall back to. */
  defaults?: EstimateInputs;
}
/** The three assumptions behind the revenue estimate. `ads_per_pageview`
 *  is absent on estimates saved before it existed; treat that as 1. */
export interface EstimateInputs { ecpm: number | null; fill_rate: number | null; ads_per_pageview: number | null }
/** `change` is the visitors percentage against the previous window of the
 *  same length, and `previous` the count it is measured from. Both null
 *  where no honest figure exists: range 'all', or nothing to divide by. */
export interface ClientStatsRow { key: string; source?: TrafficSource; connected: boolean; visitors: number | null; pageviews: number | null; change: number | null; previous: number | null; error?: boolean }
/** `compared_to` is the [start, end] of that previous window — null for 'all'. */
export interface ClientStats { range: StatsRange; compared_to: [string, string] | null; sites: ClientStatsRow[] }
/** An audited write. `meta` is per-action and free-form. */
export interface AuditEntry {
  id: number; ts: string; token_id: string | null; client_id: string | null;
  key: string | null; action: string; target: string | null;
  meta: Record<string, unknown> | null; request_id: string | null;
}
/** A preview link. The URL (with its token) comes back ONLY from the
 *  POST that mints it — the store keeps a hash, so it cannot be re-shown. */
export interface PreviewLink {
  id: string; draft_id: string; label: string | null;
  created_at: string; expires_at: string; revoked_at: string | null; host_suffix: string;
  /** Yours, i.e. minted from your own draft. Drafts are one per person,
   *  so only your own links can be judged live or stale from here. */
  mine: boolean;
}
/**
 * An API token as the listing shows it — never the secret, which exists
 * only in the mint response. `user_id` is what separates a person's
 * login session from a machine credential: sessions are minted by
 * logging in, carry no stored scopes, and revoking one signs that
 * person out.
 */
export interface ApiToken {
  id: string; prefix: string; label: string;
  client_id: string | null; site_key: string | null;
  scopes: string[]; expires_at: string | null;
  created_by: string | null; created_at: string;
  last_used_at: string | null; revoked_at: string | null;
  user_id: string | null;
  /** A `fleet` token: the pool it reports for. */
  pool?: string | null;
}
/**
 * Fleet health (staff only; `GET /v1/fleet`, `GET /v1/fleet/:pool`,
 * api/src/fleet.mjs). Each pool box reports once a minute; the API
 * judges the box and each site container, and `probe=1` adds a live
 * call to each site.
 */
export type FleetStatus = 'ok' | 'degraded' | 'stale' | 'down' | 'no_reports';
export type FleetLevel = 'ok' | 'warn' | 'fail';
type FleetContainerState = 'running' | 'restarting' | 'exited' | 'paused' | 'created' | 'dead' | 'removing' | 'missing';
export interface FleetContainer {
  slug: string; state: FleetContainerState; health: 'healthy' | 'unhealthy' | 'starting' | 'none';
  started_at: number | null; mem_current: number | null; mem_max: number | null; pressure_avg60: number | null;
  cpu_usec: number | null; cpu_pct: number | null; store_bytes: number | null; snapshot_at: number | null; restarts: number[];
}
interface FleetProbe {
  state: 'ok' | 'unreachable' | 'not_datahouse' | 'store_not_ready' | 'no_secret' | 'needs_update';
  via: 'sync' | 'health'; reason?: string; ready?: boolean | null; lag_seconds?: number | null; last_sweep_at?: string | null;
}
export interface FleetSite {
  slug: string; client: string | null; key: string | null; name: string; domain: string | null;
  container: FleetContainer | null; probe: FleetProbe | null; level: FleetLevel; reasons: string[];
}
interface FleetDisk { avail: number; size: number; where?: 'volume' | 'droplet-disk' }
/** The box's own fetch of one live member's public home page through Cloudflare, once a minute; `status` 0 is no answer in 15 s. */
export interface FleetPage { slug: string; status: number; ms: number }
interface FleetBox {
  uptime_s: number; cpus: number; load: [number, number, number]; cpu_pct: number | null;
  mem: { total: number; available: number };
  disk: { data: FleetDisk | null; snapshots: FleetDisk | null };
  redis: { used: number; max: number | null } | null;
  services: Record<'redis' | 'cron' | 'autoheal' | 'tunnel', string>;
  reboot_required_since?: number | null;
  /** Absent from a reporter before headless-cli#309; null when the pool has no live site to ask. */
  page?: FleetPage | null;
}
export interface FleetPool {
  name: string; status: FleetStatus; reported_at: number | null; ingested_at: number | null; age_s: number | null;
  box: FleetBox | null; reasons: string[]; counts: Record<FleetLevel, number>; sites: FleetSite[];
  owner: string; shared: boolean; pool_status: string; region: string | null; size: string | null; probed: boolean;
}
export interface FleetResponse { now: number; pools: FleetPool[] }
export const FLEET_HOURS = [1, 6, 24, 72, 168] as const;
export type FleetHours = (typeof FLEET_HOURS)[number];
export interface FleetSample {
  bucket: number; ingested_at: number; at: number;
  box: { cpu: number | null; mem: number; mem_total: number; load1: number; data_avail: number | null };
  sites: Record<string, { mem: number | null; cpu: number | null; psi: number | null; h: string }>;
}
export interface FleetPoolResponse { now: number; hours: FleetHours; bucket_s: number; pool: FleetPool; samples: FleetSample[] }
/** The mint response — the only time `token` is ever returned. */
export interface MintedToken {
  id: string; token: string; preset: string; label: string;
  client_id: string | null; site_key: string | null; expires_at: string | null;
}
/** One published version. `updated_by` is the token that wrote it. */
export interface BundleVersion { version: number; updated_at: string; updated_by: string | null; bytes: number }
/** What the SPA needs before anyone is signed in. The two suffixes are
 *  the site hosts' (lib/hosts.ts): `<key>.<client>.` + public is the
 *  CNAME target, + preview is what the customiser frames. */
export interface ConsoleConfig {
  env: string; clerk_publishable_key: string | null; github_login: boolean; public_host_suffix: string; preview_host_suffix: string;
  /** Who sees Search (Google) here: everyone, staff only, or nobody (not configured). Absent from an older API = off. */
  google_search?: 'all' | 'staff' | null;
}

/**
 * Google: a publisher's own Search Console and GA4, connected per
 * organisation (api/src/google.mjs). A connection never carries its
 * token — only who it is and what it was granted.
 */
export const GOOGLE_SCOPE_SEARCH_CONSOLE = 'https://www.googleapis.com/auth/webmasters.readonly';
export const GOOGLE_SCOPE_ANALYTICS = 'https://www.googleapis.com/auth/analytics.readonly';
export interface GoogleConnection {
  id: string; google_email: string | null; scopes: string[]; status: 'ok' | 'revoked';
  connected_by: string | null; connected_by_name: string | null; created_at: string; updated_at: string;
}
export interface GoogleConnections { connections: GoogleConnection[]; sites: Array<{ key: string; connection_id: string; gsc_property: string | null; ga4_property: string | null }> }
export interface SiteGoogle {
  connection_id: string | null; gsc_property: string | null; ga4_property: string | null;
  connection: { id: string; google_email: string | null; status: 'ok' | 'revoked' } | null;
}
/** One source's import state (console-seo import_state). Dates are YYYY-MM-DD. */
export interface SearchImportSource {
  source: 'gsc' | 'ga4' | 'plausible'; target: string; since: string | null; through: string | null;
  backfill_done: number; last_run_at: string | null; last_ok_at: string | null; last_error: string | null; rows_last_run: number | null;
}
/** GET …/search/status. `importing`: an import chain is running for the site. */
export interface SearchStatus { sources: SearchImportSource[]; importing: boolean }

/** Search Console metrics for one window. CTR is a fraction; position is Google's impression-weighted average. */
interface SearchMetrics { clicks: number; impressions: number; ctr: number | null; position: number | null }
/** A query or page row: this window, and the one before it. */
export interface SearchRow extends SearchMetrics { query?: string; page?: string; previous: SearchMetrics; delta?: number }
export interface SearchList { top: SearchRow[]; risers: SearchRow[]; fallers: SearchRow[] }
interface SearchPageQuery { page: string; clicks: number; impressions: number; position: number | null; share: number }
/** GET …/search/report (api/src/search-report.mjs). Days are YYYY-MM-DD. */
export interface SearchReport {
  latest: string;
  window: [string, string];
  compared_to: [string, string];
  totals: {
    current: SearchMetrics; previous: SearchMetrics;
    /** Percentages, except position: the difference in places (negative is better). */
    change: { clicks: number | null; impressions: number | null; ctr: number | null; position: number | null };
  };
  traffic: { source: 'ga4' | 'plausible'; metric: 'sessions' | 'visitors'; current: number | null; previous: number | null; change: number | null } | null;
  trend: Array<{ date: string; clicks: number; impressions: number; position: number | null; traffic: number | null }>;
  queries: SearchList;
  pages: SearchList;
  opportunities: {
    striking_distance: Array<SearchRow & { page: string | null }>;
    low_ctr: Array<SearchRow & { page: string | null; expected_ctr: number; missed_clicks: number }>;
    losing_pages: Array<SearchRow & { lost_clicks: number }>;
    split_queries: Array<{ query: string; impressions: number; clicks: number; pages: SearchPageQuery[] }>;
  };
  thresholds: { impressions: number; clicks: number };
  /** Page URL → the post at that path, from the site's datahouse. Null when the site could not answer. */
  posts: Record<string, { title: string | null; date_gmt: string | null; type: string | null }> | null;
}
export type SearchReportRange = '7d' | '28d';
/** A Search recommendation card (api/src/search-recs.mjs). `queries`/`pages` are the report rows it is about. */
export interface SearchRec {
  id: string;
  kind: 'striking_distance' | 'low_ctr' | 'losing_clicks' | 'split_query' | 'content' | 'technical' | 'other';
  title: string; why: string; actions: string[];
  impact: 'high' | 'medium' | 'low'; effort: 'high' | 'medium' | 'low';
  queries: string[]; pages: string[];
  status: 'open' | 'done' | 'dismissed'; status_at: string | null; created_at: string;
}
/** GET …/search/recommendations. */
export interface SearchRecs {
  run: { id: string; model: string; window_from: string | null; window_to: string | null; summary: string | null; created_at: string; finished_at: string | null } | null;
  /** A newer run is still being written. */
  pending: boolean;
  /** The newest run failed; `run` is then the last good one. */
  failed: { error: string | null; at: string | null } | null;
  recommendations: SearchRec[];
  /** Whether this environment can write recommendations at all (ANTHROPIC_API_KEY). */
  configured: boolean;
}

export interface GoogleProperties {
  search_console: { sites: Array<{ site_url: string; permission: string }> } | { error: string };
  analytics: { properties: Array<{ property: string; name: string; account: string | null }> } | { error: string };
}

/**
 * content-network. A "member" is a site that participates in the
 * network; its key is `<org>--<site>`, the console's own org and site
 * flattened (a slash cannot survive a Docker service name or a URL query
 * value, and headless-cli#99 forbids `--` inside either half, which is
 * what makes the mapping exact).
 *
 * Everything here goes through /v1/clients/:c/content-network/*, which
 * proxies to the network's internal admin API. The network never sees
 * Clerk — the control plane derives the caller's scope from their
 * memberships and states it on the request — so these responses are
 * already narrowed to what the caller may see.
 */
export interface NetworkMember {
  key: string;
  name: string;
  domain: string;
  verticals: string[];
  feedUrl?: string;
  wpDomain?: string;
  imageHosts: string[];
  logoUrl?: string;
  org?: string;
  siteKey?: string;
  status: 'active' | 'paused';
  hasRules: boolean;
  createdAt: number;
  updatedAt: number;
  /** The most recent crawl and the article count, ON the member — the
   *  list endpoint carries them per row, not as sidecars. Absent on the
   *  detail endpoint. */
  lastRun?: NetworkIngestRun | null;
  items?: number;
}

export interface NetworkRules {
  blockedDomains: string[];
  blockedKeywords: string[];
  blockedVerticals: string[];
  minQuality: number | null;
  maxAgeDays: number | null;
  ownMix: number | null;
}

export interface NetworkMemberDetail {
  publisher: NetworkMember;
  rules: NetworkRules;
  /** Non-empty = the rules row failed to parse and the member is withheld from serving. */
  rulesCorrupt: string[];
  hasRulesRow: boolean;
}

/** One crawl of one member, as the network reports it (`ingest/runs`,
 *  and `lastRun` on each member). `ok: false` comes with `error`. */
export interface NetworkIngestRun {
  publisher: string;
  startedAt: number;
  ok: boolean;
  source?: string;
  itemsSeen: number;
  itemsNew: number;
  error: string | null;
}

export interface NetworkMembersResponse { publishers: NetworkMember[] }

/**
 * The staff Data page (#134): a site's datahouse store as a query
 * surface. Both go through /v1/clients/:c/sites/:s/datahouse/*, which
 * proxies to the site's own /api/admin/query* — the store is a SQLite
 * file beside the app, and the site is the only thing that can open it.
 */
export interface DatahouseColumn { name: string; type: string; notnull: boolean; pk: boolean }
export interface DatahouseTable { name: string; kind: 'table' | 'view'; rows: number | null; columns: DatahouseColumn[] }
export interface DatahouseSchema { tables: DatahouseTable[] }
type DatahouseValue = string | number | null;
export interface DatahouseQueryResult {
  columns: string[];
  rows: DatahouseValue[][];
  /** More rows matched than the site's cap (500) returned. */
  truncated: boolean;
  ms: number;
}
